import React, { useRef, useState } from "react";
import { X, Download, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

const TEMPLATE_HEADERS = [
  "Title",
  "Description",
  "Type (property/siteVisit/lead/call/other)",
  "Assigned To Email",
  "Target Date (YYYY-MM-DD)",
  "Priority (low/medium/high)",
  "Notes",
];

const SAMPLE_ROWS = [
  ["Follow up with client", "Call client regarding site visit", "call", "agent@example.com", "2026-07-01", "high", ""],
  ["Schedule site visit", "", "siteVisit", "agent2@example.com", "2026-07-05", "medium", "Confirm availability first"],
];

function downloadTemplate() {
  // Build CSV so there's no extra dependency needed
  const lines = [TEMPLATE_HEADERS, ...SAMPLE_ROWS].map(row =>
    row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "task_bulk_import_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function TaskBulkImportModal({ onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setError("Please upload an .xlsx, .xls, or .csv file.");
      return;
    }
    setFile(f);
    setError(null);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post("https://apicrm.zenrealestate.in/api/tasks/bulk", formData, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        },
      });
      setResult(res.data);
      if (res.data.created > 0) onImported();
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* Header */}
        <div style={headerRow}>
          <h2 style={titleStyle}>Bulk Import Tasks</h2>
          <button style={closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        {/* Instructions */}
        <div style={infoBox}>
          <p style={infoText}>
            Upload an Excel (.xlsx / .xls) or CSV file to create multiple tasks at once.
            Download the template below to see the required format.
          </p>
          <p style={infoText}><strong>Required columns:</strong> Title, Assigned To Email, Type</p>
          <p style={infoText}><strong>Type values:</strong> property Â· siteVisit Â· lead Â· call Â· other</p>
          <p style={infoText}><strong>Priority values:</strong> low Â· medium Â· high (default: medium)</p>
        </div>

        {/* Template download */}
        <button style={templateBtn} onClick={downloadTemplate}>
          <Download size={16} style={{ marginRight: 6 }} />
          Download Template
        </button>

        {/* File picker */}
        <div style={uploadArea} onClick={() => fileInputRef.current.click()}>
          <Upload size={32} color="#6B7280" />
          <p style={uploadText}>
            {file ? file.name : "Click to choose file or drag & drop"}
          </p>
          <p style={uploadSubtext}>.xlsx Â· .xls Â· .csv â€” max 5 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={errorBox}>
            <AlertCircle size={16} style={{ marginRight: 6, flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={result.failed > 0 ? warnBox : successBox}>
            <div style={resultHeader}>
              <CheckCircle2 size={16} style={{ marginRight: 6, flexShrink: 0 }} />
              {result.created} task{result.created !== 1 ? "s" : ""} created
              {result.failed > 0 && `, ${result.failed} row${result.failed !== 1 ? "s" : ""} failed`}
            </div>
            {result.errors && result.errors.length > 0 && (
              <ul style={errorList}>
                {result.errors.map((e, i) => (
                  <li key={i}>Row {e.row}{e.email ? ` (${e.email})` : ""}: {e.errors.join(", ")}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={actionRow}>
          <button style={cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={file && !uploading ? uploadBtn : uploadBtnDisabled}
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? "Uploadingâ€¦" : "Import Tasks"}
          </button>
        </div>
      </div>
    </div>
  );
}

// â”€â”€ Styles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};
const modal = {
  background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 520,
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto",
};
const headerRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 };
const titleStyle = { margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" };
const closeBtn = { background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 4 };
const infoBox = { background: "#F0F4FF", borderRadius: 8, padding: "12px 14px", marginBottom: 16 };
const infoText = { margin: "2px 0", fontSize: 13, color: "#374151" };
const templateBtn = {
  display: "flex", alignItems: "center", background: "#EFF6FF", border: "1px solid #BFDBFE",
  color: "#1D4ED8", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
  cursor: "pointer", marginBottom: 16,
};
const uploadArea = {
  border: "2px dashed #D1D5DB", borderRadius: 10, padding: "28px 20px",
  textAlign: "center", cursor: "pointer", marginBottom: 16, background: "#FAFAFA",
  transition: "border-color 0.2s",
};
const uploadText = { margin: "10px 0 4px", fontSize: 14, color: "#374151", fontWeight: 500 };
const uploadSubtext = { margin: 0, fontSize: 12, color: "#9CA3AF" };
const errorBox = {
  display: "flex", alignItems: "flex-start", background: "#FEF2F2", border: "1px solid #FECACA",
  color: "#DC2626", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14,
};
const successBox = {
  background: "#F0FDF4", border: "1px solid #86EFAC",
  color: "#15803D", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14,
};
const warnBox = {
  background: "#FFFBEB", border: "1px solid #FCD34D",
  color: "#92400E", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14,
};
const resultHeader = { display: "flex", alignItems: "center", fontWeight: 600, marginBottom: 6 };
const errorList = { margin: "6px 0 0", paddingLeft: 20, fontSize: 12 };
const actionRow = { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 };
const cancelBtn = {
  padding: "9px 20px", borderRadius: 8, border: "1px solid #D1D5DB",
  background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer", fontSize: 14,
};
const uploadBtn = {
  padding: "9px 22px", borderRadius: 8, border: "none",
  background: "#1D4ED8", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: 14,
};
const uploadBtnDisabled = {
  ...uploadBtn, background: "#93C5FD", cursor: "not-allowed",
};
